import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Radar,
  RadarChart,
  PolarGrid,
  Legend,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { SFStack, SFUseStyled, SFTypography } from "@sf-core";
import ProficiencyLevelBar from "../../components/proficiencyLevelBar";

const { styled } = SFUseStyled();

const ChartContainerStyled = styled("div")(({ theme }) => ({
  ".recharts-polar-angle-axis-tick-value1": {
    color: "#C4C4CD",
    fontFamily: "EYInterstate",
    fontSize: 12,
    fontWeight: 300,
  },
}));

const StyledCircle = styled("div")(({ theme, color }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: color,
}));

const TooltipStyled = styled("div")(({ theme }) => ({
  background: "rgba(255,255,255,0.98)",
  filter: "drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.25))",
  border: "1px rgba(0, 0, 0, 0.25) solid",
  padding: theme.spacing(1),
  borderRadius: 4,
  color: theme.palette.grey400.main,
  maxWidth: 340,
  ".title": {
    fontSize: "14px",
    fontWeight: 400,
  },
  ".sub-title": {
    fontSize: "11px",
    fontWeight: 300,
    marginBottom: 10,
  },
  ".proficiency-level": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    ".proficiency-level-item": {
      display: "flex",
      alignItems: "center",
      color: theme.palette.grey300.main,
      fontSize: "12px",
      fontWeight: 300,
      padding: "5px 10px",
      svg: {
        marginRight: "5px",
      },
    },
  },
}));

function getSvgTextLength(textContent) {
  // Create a new SVG element
  const svgNS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(svgNS, "svg");

  // Create a new text element
  const text = document.createElementNS(svgNS, "text");
  text.textContent = textContent;

  // Append the text to the SVG, and the SVG to the body
  svg.appendChild(text);
  document.body.appendChild(svg);

  // Measure the text length
  const length = text.getComputedTextLength();

  // Clean up by removing the SVG from the body
  document.body.removeChild(svg);

  return length;
}

const SkillComparison = ({ skills }) => {
  const { t } = useTranslation();
  const chartContainerRef = useRef(null); // Ref for the chart container
  const [chartWidth, setChartWidth] = useState(0); // State to store the chart width

  useEffect(() => {
    // Function to update the chart width
    const updateChartWidth = () => {
      if (chartContainerRef.current) {
        setChartWidth(chartContainerRef.current.offsetWidth);
      }
    };

    // Update chart width on mount and add resize event listener
    updateChartWidth();
     
    window.addEventListener("resize", updateChartWidth);

  }, []);

  const PROFICIENCY_LABELS = [
    t("lxpComponents.proficiencyLevel.entry"),
    t("lxpComponents.proficiencyLevel.intermediate"),
    t("lxpComponents.proficiencyLevel.advanced"),
    t("lxpComponents.proficiencyLevel.mastery"),
  ];

  function customTick({ payload, x, y, textAnchor, stroke, radius, ...props }) {
    let textLength = getSvgTextLength(PROFICIENCY_LABELS[payload.value]);
    if (textLength == 0) return null;

    return (
      <g className="recharts-layer recharts-polar-angle-axis-tick">
        <rect
          x={x - textLength / 2 - 5}
          y={y - 56}
          rx={2}
          ry={2}
          width={textLength + 10}
          height={15}
          fill="#fefefe"
          opacity={0.7}
        ></rect>
        <text
          radius={radius}
          stroke={stroke}
          x={x}
          y={y - 44}
          className="recharts-text recharts-polar-angle-axis-tick-value1"
          fontFamily="EYInterstate"
          fontSize="30px"
          textAnchor={textAnchor}
        >
          {PROFICIENCY_LABELS[payload?.value]}
        </text>
      </g>
    );
  }

  const CustomLegend = ({ payload }) => {
    return (
      <SFStack
        direction="row"
        spacing={4}
        justifyContent="center"
        style={{ width: "100%" }}
      >
        <SFStack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <StyledCircle color="#35A4E8" />
          <SFTypography>
            {t("dashboardScreen.skillComparison.currentProficiencyLevels")}
          </SFTypography>
        </SFStack>
        <SFStack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <StyledCircle color="#FF810A" />
          <SFTypography>
            {t("dashboardScreen.skillComparison.targetLevelsForRole")}
          </SFTypography>
        </SFStack>
      </SFStack>
    );
  };

  function wrapSvgText(text) {
    const maxWidth = chartWidth * 0.15;
    const words = text.split(/\s+/);
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = getSvgTextLength(currentLine + " " + word);
      if (width < maxWidth) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);

    return lines;
  }

  function customAngleTick({ payload, x, y, textAnchor, ...props }) {
    const maxTextWidth = chartWidth * 0.25; // Maximum width in pixels that the text can occupy
    let displayText = payload.value;

    // Measure the text length
    let textLength = getSvgTextLength(displayText);

    // Truncate text if it's too long
    if (textLength > maxTextWidth && displayText.length > 0) {
      while (textLength > maxTextWidth && displayText.length > 0) {
        displayText = displayText.slice(0, -1);
        textLength = getSvgTextLength(displayText + "...");
      }
      displayText += "...";
    }

    const lineHeight = 12; // Line height in pixels
    const lines = wrapSvgText(displayText); // Split text into lines
    const textOffsetX = 15; // Offset to move text away from rectangle horizontally
    const textOffsetY = 20; // Offset to move text away from rectangle vertically
    // Adjust vertical position based on textAnchor and quadrant
    let newY = y;
    if (
      payload?.coordinate <= -240 ||
      (payload?.coordinate >= 50 && payload?.coordinate <= 180)
    ) {
      // Labels on the top side
      newY -= textOffsetY;
    }
    if (payload?.coordinate >= -150 && payload?.coordinate <= -60) {
      // Labels on the bottom side
      newY += textOffsetY;
    }

    return (
      <g className="recharts-layer recharts-polar-angle-axis-tick">
        <rect
          x={x - 5}
          y={y - 5}
          width={10}
          height={10}
          fill="rgba(35, 35, 47, 1)"
          stroke="#FFF"
          strokeWidth={1}
          ry={2}
          rx={2}
        />
        {lines.map((line, index) => (
          <text
            x={textAnchor === "end" ? x - textOffsetX : x + textOffsetX}
            y={newY + 4 + index * lineHeight}
            textAnchor={textAnchor}
            dominantBaseline="top"
            fill="#C4C4CD"
            fontSize={11}
          >
            {line}
          </text>
        ))}
      </g>
    );
  }

  const CustomTooltip = ({ active, payload, label, ...props }) => {
    if (active && payload) {
      const skillAboveTarget = t(
        "dashboardScreen.skillComparison.skillAboveTarget",
        {
          returnObjects: true,
          skillName: label,
        }
      );
      const skillSameAsTarget = t(
        "dashboardScreen.skillComparison.skillSameAsTarget",
        {
          returnObjects: true,
          skillName: label,
        }
      );
      const skillBelowTarget = t(
        "dashboardScreen.skillComparison.skillBelowTarget",
        {
          returnObjects: true,
          skillName: label,
        }
      );

      const getFeedback = (payload) => {
        let profLevel = payload?.proficiencyLevel - 1;
        if (payload?.proficiencyLevel > payload?.proficiencyLevelTarget) {
          return skillAboveTarget[profLevel]?.label;
        } else if (
          payload.proficiencyLevel === payload.proficiencyLevelTarget
        ) {
          return skillSameAsTarget[profLevel]?.label;
        } else {
          return skillBelowTarget[profLevel]?.label;
        }
      };

      return (
        <TooltipStyled>
          <div className="title">{label}</div>
          <div className="sub-title">{payload[0]?.payload.skillGroupName}</div>
          <div className="feedback">{getFeedback(payload[0]?.payload)}</div>
          <div className="proficiency-level">
            {payload?.map((a) => (
              <div className="proficiency-level-item">
                <ProficiencyLevelBar
                  type={a.dataKey}
                  proficiencyLevel={a.value}
                />
              </div>
            ))}
          </div>
        </TooltipStyled>
      );
    }

    return null;
  };

  return (
    <ChartContainerStyled ref={chartContainerRef}>
      <SFStack alignItems="center" justifyContent="center">
        <ResponsiveContainer width="100%" height={600}>
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius="75%"
            data={skills?.filter((skill) => skill.isJobRoleSkill)}
            width="100%"
            height={600}
          >
            <Tooltip content={<CustomTooltip />} />
            <PolarGrid
              stroke="#747480"
              axisLine={{
                strokeWidth: 1,
                //stroke: "rgba(234, 234, 242, 0.1)",
                stroke: "#2E2E38",
              }}
            />
            <PolarAngleAxis
              dataKey="skillName"
              stroke="#C4C4CD"
              axisLine={{
                strokeWidth: 1,
                stroke1: "rgba(234, 234, 242, 0.1)",
                stroke: "#2E2E38",
              }}
              tick={customAngleTick}
              orientation="outer"
            />
            <Radar
              name={t("dashboardScreen.skillComparison.proviciencyLevel")}
              dataKey="proficiencyLevel"
              stroke="#35A4E8"
              fill="#35A4E8"
              fillOpacity={0.5}
              dot={{ r: 5, fillOpacity: 0.8 }}
            />
            <Radar
              name={t("dashboardScreen.skillComparison.proviciencyLevelTarget")}
              dataKey="proficiencyLevelTarget"
              stroke="#FF810A"
              fill="#FF810A"
              fillOpacity={0.2}
              dot={{ r: 5, fillOpacity: 0.8 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 4]}
              ticks={[0, 1, 2, 3, 4]}
              orientation="outer"
              tick={customTick}
            />
            <Legend content={<CustomLegend />} />
          </RadarChart>
        </ResponsiveContainer>
      </SFStack>
    </ChartContainerStyled>
  );
};

export default SkillComparison;
